/**
 * Money on a customer surface.
 *
 * MASTER_SPEC 0.5.6 stores money as signed integer minor units. Design Bible
 * 10.3 adds the rule this file exists to enforce: *never render a computed
 * value the ledger has not computed*. So there is no `sum()` here, no tax
 * estimator, and no "you save" arithmetic. The storefront displays the integers
 * core sent, in the order core sent them, and the one derived number it is
 * allowed to show — the itemised total — is checked against core's own
 * `total_minor` rather than replacing it.
 *
 * Nothing here goes through a float. `Number("12.10") * 100` is
 * 1209.9999999999998 and a four-figure slab is not a value to round-trip
 * through that.
 */

export const MINOR_PER_UNIT = 100;

export function formatUsdMinor(minor: number): string {
  const negative = minor < 0;
  const absolute = Math.abs(minor);
  const units = Math.trunc(absolute / MINOR_PER_UNIT);
  const cents = absolute % MINOR_PER_UNIT;
  const grouped = new Intl.NumberFormat("en-US", { useGrouping: true }).format(units);
  return `${negative ? "-" : ""}$${grouped}.${String(cents).padStart(2, "0")}`;
}

/** Schema.org and other machine consumers want the decimal string, not the glyph. */
export function decimalUsdMinor(minor: number): string {
  const units = Math.trunc(Math.abs(minor) / MINOR_PER_UNIT);
  const cents = Math.abs(minor) % MINOR_PER_UNIT;
  return `${minor < 0 ? "-" : ""}${String(units)}.${String(cents).padStart(2, "0")}`;
}

export interface TotalCheck {
  /** The sum of the parts core itemised. */
  readonly itemisedMinor: number;
  /** True when core's own total agrees with its own itemisation. */
  readonly agrees: boolean;
}

/**
 * Recomputes core's itemisation and compares it with core's total.
 *
 * This is not the storefront deciding what to charge. It is the storefront
 * refusing to show a total whose parts do not add up: if these disagree the
 * checkout blocks rather than asking someone to approve a number the server
 * contradicts itself about.
 */
export function checkItemisedTotal(state: {
  readonly item_subtotal_minor: number;
  readonly discount_minor: number;
  readonly shipping_minor: number;
  readonly insurance_minor: number;
  readonly tax_minor: number;
  readonly total_minor: number;
}): TotalCheck {
  const itemisedMinor =
    state.item_subtotal_minor -
    state.discount_minor +
    state.shipping_minor +
    state.insurance_minor +
    state.tax_minor;
  return { itemisedMinor, agrees: itemisedMinor === state.total_minor };
}
